import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules/gatorProtect';
import {
  PlansBanner,
  ProtectionCardFactory,
  Summary,
} from './components';

import ChartSection from './sections/ChartSection/ChartSection';
import ThreatsTableSection from './sections/ThreatsTableSection/ThreatsTableSection';
import * as Styles from './GatorProtectHandler.styles';

import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import ProtectionModal from './components/ProtectionModal/ProtectionModal';
import UpgradeCycleModal from './components/UpgradeCycleModal/UpgradeCycleModal';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from './GatorProtect.enum';
import { parseDiscountPercent, useGatorProtectProPlan } from './hooks/useGatorProtectProPlan';
import { parseGatorProtectUpgradeHostingId } from '@/redux/modules/gatorProtect/gatorProtect.buy.utils';
import { getIsoDateStringDaysAgo } from '@/utils/date-time/getIsoDateStringDaysAgo';
import { useHistory, useParams } from 'react-router';
import { getSelectedPlanInfo } from './utils/getSelectedPlanInfo';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  MONETIZACAO_GATOR_PROTECT_TRIAL_AVAILABLE,
  MONETIZACAO_MODAL_UPGRADE_GATOR_PROTECT_CYCLE,
  MONETIZACAO_SHOW_EXPORT_GATORPROTECT_BUTTON,
  MONETIZACAO_THREATS_TABLE_SECTION,
} from '@/config/GrowthBook/constants';
import { loadCreditCard } from '@/redux/actions/creditCard';

const TRIAL_ACTIVATE = 'TrialActivate';
const PRO_ACTIVATE = 'ProActivate';

/**
 * Deep link: `/:productId` (route param) pre-selects the hosting
 * when opening the panel (e.g. threats alert from home).
 */
const GatorProtectHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gatorProtect: gatorProtectLocale,
    routes: routesLocale,
  } = useLocale();
  const params = useParams();
  const { loading: gatorProtectLoading, plans, planDetails } = useSelector(state => state.gatorProtect);
  const { loading: fastCheckoutLoading } = useSelector(state => state.fastCheckout);
  const enableTrial = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_TRIAL_AVAILABLE);
  const summary = useSelector(state => state.summary);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [protectionModal, setProtectionModal] = useState({
    open: false,
    activationKey: null,
    acceptedTermsProduct: null,
  });
  const [upgradeCycleModalOpen, setUpgradeCycleModalOpen] = useState(false);
  const [protectionModalTermsAgreed, setProtectionModalTermsAgreed] = useState(false);
  const {
    monthly,
    annual,
    triennial,
    availableCycles,
    isLoadingPlanData,
  } = useGatorProtectProPlan();

  const protectionCardUpgradeCycleDiscountPercent = parseDiscountPercent(annual?.discountPercent);

  const summaryState = useSelector(state => state.summary);
  const creditCard = useSelector(state => state.creditCard);
  const withThreatsTable = useFeatureIsOn(MONETIZACAO_THREATS_TABLE_SECTION);
  const withExportButton = useFeatureIsOn(MONETIZACAO_SHOW_EXPORT_GATORPROTECT_BUTTON);
  const isUpgradeCycleModalEnabled = useFeatureIsOn(MONETIZACAO_MODAL_UPGRADE_GATOR_PROTECT_CYCLE);

  const loading = gatorProtectLoading || creditCard?.loading;
  const hasCreditCard = (creditCard?.cardType) || false;
  const forceOpenPaymentOptions = hasCreditCard ? '' : '?openInvoice=true';

  const {
    isTrialActive,
    isTrialExpired,
    isProActive,
    isProOnMonthlyOrAnnualCycle,
    isAAClient,
    hasClientUsedTrial,
    isTurbo,
    hasInvoice,
    invoiceId,
  } = getSelectedPlanInfo(selectedPlan);

  const invoiceHref = `${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}${forceOpenPaymentOptions}`;

  const getProPrice = useCallback(() => {
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch]);

  useEffect(() => { getProPrice(); }, [getProPrice]);

  const checkIfClientHasCreditCard = useCallback(() => {
    dispatch(loadCreditCard());
  }, [dispatch]);

  useEffect(() => {
    checkIfClientHasCreditCard();
  }, [checkIfClientHasCreditCard]);

  const getPlansByServer = useCallback(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  useEffect(() => { getPlansByServer(); }, [getPlansByServer]);


  const preventAccessIfClientCantSeeGatorProtect = useCallback(() => {
    if (!summary?.features?.gator_protect) {
      history.push(routesLocale.home);
    }
  }, [history, routesLocale, summary]);

  useEffect(() => {
    preventAccessIfClientCantSeeGatorProtect();
  }, [preventAccessIfClientCantSeeGatorProtect]);

  const requestPlanThreatsList = useCallback((page) => {
    if (selectedPlan?.shared_hosting_id) {
      dispatch(gatorProtectActions.planThreats.request({
        hostingId: selectedPlan?.shared_hosting_id,
        page: page || 1,
        perPage: 10,
      }));
    }
  }, [dispatch, selectedPlan?.shared_hosting_id]);

  useEffect(() => {
    requestPlanThreatsList();
  }, [requestPlanThreatsList]);

  const handleOnChangePlan = (plan) => {
    setSelectedPlan(plan);
    const formattedStartDate = getIsoDateStringDaysAgo(30);

    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  };

  const closeProtectionModal = () => {
    setProtectionModal({ open: false, activationKey: null, acceptedTermsProduct: null });
  };

  useEffect(() => {
    if (!protectionModal.open) {
      setProtectionModalTermsAgreed(false);
    }
  }, [protectionModal.open, protectionModal.activationKey]);

  const openProtectionProModal = () => {
    setProtectionModal({
      open: true, activationKey: 'activateProtectionPro', acceptedTermsProduct: PRO_ACTIVATE,
    });
  };

  const openProtectionTrialModal = () => {
    setProtectionModal({
      open: true, activationKey: 'activateTrial', acceptedTermsProduct: TRIAL_ACTIVATE,
    });
  };

  const handleUpgradeCycleClick = useCallback(() => {
    if (!isProActive || !isUpgradeCycleModalEnabled) {
      return;
    }
    setUpgradeCycleModalOpen(true);
  }, [isProActive, isUpgradeCycleModalEnabled]);

  useEffect(() => {
    if (upgradeCycleModalOpen && (!isProActive || !isUpgradeCycleModalEnabled)) {
      setUpgradeCycleModalOpen(false);
    }
  }, [upgradeCycleModalOpen, isProActive, isUpgradeCycleModalEnabled]);

  const closeUpgradeCycleModal = useCallback(() => {
    setUpgradeCycleModalOpen(false);
  }, []);

  const handleUpgradeCycleModalConfirm = useCallback((cycleKey) => {
    if (!isProActive) {
      return;
    }

    const billingCycle = cycleKey ?? 'monthly';

    const redirectToInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}`);
    };

    const upgradeHostingId = parseGatorProtectUpgradeHostingId(selectedPlan?.gatorprotect_hosting_id);
    if (upgradeHostingId != null) {
      dispatch(gatorProtectActions.purchase.upgradeOrder.request({
        hostingId: upgradeHostingId,
        newProductId: GATOR_PROTECT_ID.PRO,
        newBillingCycle: billingCycle,
        redirectToInvoice,
      }));
    } else {
      dispatch(gatorProtectActions.purchase.fastCheckout.request({
        productId: GATOR_PROTECT_ID.PRO,
        domain: selectedPlan?.shared_hosting_domain,
        redirectToInvoice,
        hostingId: selectedPlan?.shared_hosting_id,
        billingCycle,
      }));
    }

    closeUpgradeCycleModal();
  }, [
    closeUpgradeCycleModal,
    dispatch,
    history,
    isProActive,
    routesLocale.invoices,
    routesLocale.unpaid,
    selectedPlan,
  ]);

  const upgradeCycleModalContent = gatorProtectLocale?.summary?.upgradeCycleModal;

  const upgradeCycleOptions = useMemo(() => {
    const c = upgradeCycleModalContent;
    if (!c || !availableCycles?.length) {
      return [];
    }
    const resolvePlan = (cycleKey) => {
      if (cycleKey === 'monthly') return monthly;
      if (cycleKey === 'annually') return annual;
      return triennial;
    };
    const resolveBaseLabel = (cycleKey) => {
      if (cycleKey === 'monthly') return c.paymentCycleLabelMonthly;
      if (cycleKey === 'annually') return c.paymentCycleLabelAnnual;
      return c.paymentCycleLabelTriennial;
    };
    return availableCycles.map((cycleKey) => {
      const plan = resolvePlan(cycleKey);
      if (!plan) {
        return null;
      }
      const baseLabel = resolveBaseLabel(cycleKey);
      const label = plan.isBestOption === true
        ? `${baseLabel} | ${c.bestOptionSuffix}`
        : baseLabel;
      const tag = plan.discountPercent > 0 ? c.getDiscountBadge(plan.discountPercent) : undefined;
      return {
        cycleKey,
        label,
        tag,
        formattedPrice: plan.formattedPrice,
        discountPercent: plan.discountPercent,
      };
    }).filter(Boolean);
  }, [
    upgradeCycleModalContent,
    availableCycles,
    monthly,
    annual,
    triennial,
  ]);

  const protectionActivationOptions = useMemo(() => {
    if (protectionModal.activationKey !== 'activateProtectionPro') {
      return [];
    }
    return upgradeCycleOptions;
  }, [protectionModal.activationKey, upgradeCycleOptions]);

  const proActivationUpgradeCycleContent = useMemo(() => {
    if (!protectionModal.open || protectionModal.activationKey !== 'activateProtectionPro') {
      return null;
    }

    const modalContent = gatorProtectLocale?.summary?.protectionModal;
    const upgrade = upgradeCycleModalContent;

    if (!modalContent || !upgrade) {
      return null;
    }

    const scenario = { ...modalContent.proActivation };
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};
    const checklistItems = scenario.checklistItems || commonContent.checklistItems || [];

    const heroTitle = scenario.title;
    const heroSubtitle = [scenario.subtitle, parsedMessage].filter(Boolean).join(' ');

    let staticCtaLabel;
    if (isTurbo) {
      staticCtaLabel = typeof modalContent.proActivation.buttonLabel === 'function'
        ? modalContent.proActivation.buttonLabel(null)
        : modalContent.proActivation.buttonLabel;
    }

    const ctaPriceFallback = monthly?.formattedPrice ?? '...';

    return {
      heroTitle,
      heroSubtitle,
      featureItems: checklistItems,
      paymentCycleLabel: upgrade.paymentCycleLabel,
      selectCyclePlaceholder: upgrade.selectCyclePlaceholder,
      paymentCycleLabelMonthly: upgrade.paymentCycleLabelMonthly,
      paymentCycleLabelAnnual: upgrade.paymentCycleLabelAnnual,
      paymentCycleLabelTriennial: upgrade.paymentCycleLabelTriennial,
      bestOptionSuffix: upgrade.bestOptionSuffix,
      getDiscountBadge: upgrade.getDiscountBadge,
      termsPrefix: upgrade.termsPrefix,
      termsLink: upgrade.termsLink,
      getCtaLabel: upgrade.getCtaLabel,
      refundNote: upgrade.refundNote,
      staticCtaLabel,
      ctaPriceFallback,
    };
  }, [
    gatorProtectLocale,
    upgradeCycleModalContent,
    selectedPlan,
    monthly,
    protectionModal.open,
    protectionModal.activationKey,
    isTurbo,
  ]);

  const trialProtectionModalPayload = useMemo(() => {
    if (!protectionModal.open || protectionModal.activationKey !== 'activateTrial') {
      return null;
    }

    const modalContent = gatorProtectLocale?.summary?.protectionModal;
    const upgrade = upgradeCycleModalContent;

    if (!modalContent || !upgrade) {
      return null;
    }

    const getScenario = () => {
      if (isTrialActive) {
        return { ...modalContent.trialActive };
      }

      if (isTrialExpired) {
        return modalContent.trialActive;
      }

      return {
        ...modalContent.trialActivation,
        title: modalContent.trialActivation.title(isAAClient),
        subtitle: modalContent.trialActivation.subtitle(monthly?.formattedPrice ?? '...'),
      };
    };

    const scenario = getScenario();
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};
    const checklistItems = scenario.checklistItems || commonContent.checklistItems || [];

    const rawButton = scenario.buttonLabel;
    const buttonLabel = typeof rawButton === 'function'
      ? rawButton(monthly?.formattedPrice ?? '...')
      : rawButton;

    const content = {
      title: scenario.title,
      subtitle: scenario.subtitle,
      ...(parsedMessage ? { message: parsedMessage } : {}),
      checklistItems,
      buttonLabel,
      productTitle: commonContent.productTitle,
      productSubtitle: commonContent.productSubtitle,
      proBadgeLabel: undefined,
      mascotAlt: gatorProtectLocale?.banner?.img?.alt ?? '',
    };

    const terms = {
      label: upgrade.termsPrefix,
      cta: upgrade.termsLink,
    };

    return { content, terms };
  }, [
    gatorProtectLocale,
    upgradeCycleModalContent,
    isTrialActive,
    isTrialExpired,
    selectedPlan,
    monthly,
    isAAClient,
    protectionModal.open,
    protectionModal.activationKey,
  ]);

  const proProtectionModalPayload = useMemo(() => {
    if (
      !protectionModal.open
      || protectionModal.activationKey !== 'activateProtectionPro'
      || isUpgradeCycleModalEnabled
    ) {
      return null;
    }

    const modalContent = gatorProtectLocale?.summary?.protectionModal;
    const upgrade = upgradeCycleModalContent;

    if (!modalContent || !upgrade) {
      return null;
    }

    const scenario = { ...modalContent.proActivation };
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};
    const checklistItems = scenario.checklistItems || commonContent.checklistItems || [];

    const rawButton = scenario.buttonLabel;
    const buttonLabel = typeof rawButton === 'function'
      ? rawButton(monthly?.formattedPrice ?? '...')
      : rawButton;

    const content = {
      title: scenario.title,
      subtitle: scenario.subtitle,
      ...(parsedMessage ? { message: parsedMessage } : {}),
      checklistItems,
      buttonLabel,
      productTitle: commonContent.productTitle,
      productSubtitle: commonContent.productSubtitle,
      proBadgeLabel: commonContent.proBadgeLabel,
      mascotAlt: gatorProtectLocale?.banner?.img?.alt ?? '',
    };

    const terms = {
      label: upgrade.termsPrefix,
      cta: upgrade.termsLink,
    };

    return { content, terms };
  }, [
    gatorProtectLocale,
    upgradeCycleModalContent,
    selectedPlan,
    monthly,
    protectionModal.open,
    protectionModal.activationKey,
    isUpgradeCycleModalEnabled,
  ]);

  const handleAutomaticCleaningToggle = () => {
    const current = planDetails.auto_protect ?? true;
    const next = !current;
    dispatch(gatorProtectActions.planDetails.setAutoProtect(next));
    if (selectedPlan?.shared_hosting_id != null) {
      dispatch(gatorProtectActions.planDetails.updateAutoProtect.request({
        hostingId: selectedPlan.shared_hosting_id,
        autoProtect: next,
      }));
    }
  };

  const getSelectedPlanDetails = useCallback(() => {
    if (plans.length === 0 || selectedPlan !== null) {
      return;
    }

    const planFromRoute = params?.productId
      ? plans.find(p => p.shared_hosting_id === Number(params.productId))
      : null;
    const plan = planFromRoute ?? plans[0];
    setSelectedPlan(plan);
    const formattedStartDate = getIsoDateStringDaysAgo(30);
    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  }, [plans, selectedPlan, dispatch, params]);

  useEffect(() => { getSelectedPlanDetails(); }, [getSelectedPlanDetails]);

  const handleActivate = (activationKey, billingCycle = 'monthly') => {
    const redirectToInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}${forceOpenPaymentOptions}`);
    };

    switch (activationKey) {
      case 'activateTrial': {
        if (!enableTrial) {
          return;
        }
        dispatch(gatorProtectActions.purchase.fastCheckout.request({
          productId: GATOR_PROTECT_ID.TRIAL,
          domain: selectedPlan?.shared_hosting_domain,
          hostingId: selectedPlan?.shared_hosting_id,
          redirectToInvoice: false,
          onPurchaseSuccess: () => {
            setSelectedPlan({
              ...selectedPlan,
              is_trial: true,
              expire_days: isAAClient ? 365 : 30,
            });
            setProtectionModal({
              open: false,
              activationKey: null,
              acceptedTermsProduct: null,
            });
            dispatch(gatorProtectActions.plans.request({ headerId: null }));
            const formattedStartDate = getIsoDateStringDaysAgo(30);
            dispatch(gatorProtectActions.planDetails.request({
              hostingId: selectedPlan.shared_hosting_id,
              startDate: formattedStartDate,
            }));
          },
        }));
        break;
      }
      case 'activateProtectionPro': {
        const upgradeHostingId = parseGatorProtectUpgradeHostingId(selectedPlan?.gatorprotect_hosting_id);
        if (upgradeHostingId != null) {
          dispatch(gatorProtectActions.purchase.upgradeOrder.request({
            hostingId: upgradeHostingId,
            newProductId: GATOR_PROTECT_ID.PRO,
            newBillingCycle: billingCycle ?? 'monthly',
            redirectToInvoice,
          }));
        } else {
          dispatch(gatorProtectActions.purchase.fastCheckout.request({
            productId: GATOR_PROTECT_ID.PRO,
            domain: selectedPlan?.shared_hosting_domain,
            redirectToInvoice,
            hostingId: selectedPlan?.shared_hosting_id,
            billingCycle,
          }));
        }
        break;
      }
      default:
        break;
    }
  };

  const preventAccess = useCallback(() => {
    if (!summaryState?.features?.gator_protect) {
      history.push(routesLocale.home);
    }
  }, [history, routesLocale, summaryState]);

  useEffect(() => {
    preventAccess();
  }, [preventAccess]);

  const getSortedChartByDate = useCallback(() => {
    if (!planDetails.activity_detected) {
      return [];
    }
    const compareByDateAscending = (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime();
    return [...planDetails.activity_detected].sort(compareByDateAscending);
  }, [planDetails.activity_detected]);

  return (
    <Styles.Container data-testid="gator-protect-handler">
      <div>
        <PlansBanner
          content={gatorProtectLocale.banner}
          plans={plans}
          loading={loading || plans.length === 0}
          selectedPlan={selectedPlan}
          onChangePlan={handleOnChangePlan}
        />
        <Summary content={gatorProtectLocale.summary} loading={loading}>
          {({ Banners, Main, Alerts }) => (
            <>
              <Banners>
                {isTrialActive && <Summary.Banner type="trialActive" trialDaysLeft={(selectedPlan && selectedPlan.expire_days) || 0} />}
                {isProActive && (
                  <Summary.Banner
                    type="fullProtectionActive"
                    onFullProtectionCtaClick={isUpgradeCycleModalEnabled ? handleUpgradeCycleClick : undefined}
                  />
                )}
                {isTrialExpired && <Summary.Banner type="expiredProtection" />}
              </Banners>
              <Main>
                <Summary.Title loading={loading} />

                <Styles.DataAndMetricsRow autoColumns={isProActive}>
                  <Summary.Metrics
                    threatsIdentified={planDetails.summary.detected}
                    threatsRemediated={planDetails.summary.remediated}
                    enabled={(isProActive || isTrialActive) ? (planDetails.auto_protect ?? true) : undefined}
                    onToggle={(isProActive || isTrialActive) ? handleAutomaticCleaningToggle : undefined}
                    lastScan={planDetails?.summary?.last_scan}
                    hasToggle={isProActive || isTrialActive}
                  />

                  <ProtectionCardFactory
                    isLoading={loading}
                    hasInvoice={hasInvoice}
                    isTrialActive={isTrialActive}
                    isTrialExpired={isTrialExpired}
                    onActivateProtectionPro={openProtectionProModal}
                    onActivateTrial={openProtectionTrialModal}
                    onPendingInvoiceClick={() => history.push(invoiceHref)}
                    isAAClient={isAAClient}
                    hideActivateTrial={isTrialActive || hasClientUsedTrial}
                    isTurbo={isTurbo}
                    enableTrial={enableTrial}
                    upgradeCycleEligible={isUpgradeCycleModalEnabled && isProOnMonthlyOrAnnualCycle}
                    upgradeCycleDiscountPercent={protectionCardUpgradeCycleDiscountPercent}
                    upgradeCyclePricingLoading={isLoadingPlanData}
                    onUpgradeCycleClick={handleUpgradeCycleClick}
                  />
                </Styles.DataAndMetricsRow>
              </Main>
              <Alerts />
            </>
          )}
        </Summary>
      </div>

      <ChartSection
        sectionId="activities-chart"
        data={getSortedChartByDate()}
        isLoading={loading}
      />

      {withThreatsTable && (
        <ThreatsTableSection
          requestPlanThreatsList={requestPlanThreatsList}
          showExportButton={withExportButton}
        />
      )}

      {(upgradeCycleModalOpen && isProActive && isUpgradeCycleModalEnabled) && (
        <UpgradeCycleModal
          content={upgradeCycleModalContent}
          upgradeCycleOptions={upgradeCycleOptions}
          loading={loading || isLoadingPlanData}
          onClose={closeUpgradeCycleModal}
          onConfirm={handleUpgradeCycleModalConfirm}
        />
      )}

      {(protectionModal.open && trialProtectionModalPayload) && (
        <ProtectionModal
          content={trialProtectionModalPayload.content}
          terms={trialProtectionModalPayload.terms}
          agreed={protectionModalTermsAgreed}
          onToggleAgreed={() => setProtectionModalTermsAgreed(v => !v)}
          onButtonClick={() => {
            const hostingId = selectedPlan?.shared_hosting_id;
            if (hostingId != null && protectionModal.acceptedTermsProduct != null) {
              dispatch(gatorProtectActions.acceptTerms.request({
                hostingId,
                termsAccepted: protectionModal.acceptedTermsProduct,
              }));
            }
            handleActivate('activateTrial');
          }}
          onClose={closeProtectionModal}
          loadingData={loading || isLoadingPlanData}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}

      {(protectionModal.open && proProtectionModalPayload) && (
        <ProtectionModal
          content={proProtectionModalPayload.content}
          terms={proProtectionModalPayload.terms}
          agreed={protectionModalTermsAgreed}
          onToggleAgreed={() => setProtectionModalTermsAgreed(v => !v)}
          onButtonClick={() => {
            const hostingId = selectedPlan?.shared_hosting_id;
            if (hostingId != null && protectionModal.acceptedTermsProduct != null) {
              dispatch(gatorProtectActions.acceptTerms.request({
                hostingId,
                termsAccepted: protectionModal.acceptedTermsProduct,
              }));
            }
            handleActivate('activateProtectionPro', 'monthly');
          }}
          onClose={closeProtectionModal}
          loadingData={loading || isLoadingPlanData}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}

      {/* eslint-disable-next-line no-mixed-operators */}
      {(protectionModal.open && isUpgradeCycleModalEnabled && proActivationUpgradeCycleContent) && (
        <UpgradeCycleModal
          variant="activation"
          testId="gator-protect-protection-modal"
          showProBadge
          content={proActivationUpgradeCycleContent}
          upgradeCycleOptions={protectionActivationOptions}
          loading={loading || fastCheckoutLoading}
          onClose={closeProtectionModal}
          onConfirm={(cycleKey) => {
            const hostingId = selectedPlan?.shared_hosting_id;
            if (hostingId != null && protectionModal.acceptedTermsProduct != null) {
              dispatch(gatorProtectActions.acceptTerms.request({
                hostingId,
                termsAccepted: protectionModal.acceptedTermsProduct,
              }));
            }
            handleActivate(protectionModal.activationKey, cycleKey);
          }}
        />
      )}
    </Styles.Container>
  );
};

export default withErrorBoundary(GatorProtectHandler, 'GatorProtectHandler');
